/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving and saving a drugDispensation.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDrugDispensationState {
    drugDispensation: Dtos.DrugDispensation | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
    saveState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDrugDispensationState {
    drugDispensation: IInitialDrugDispensationState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DrugDispensationInitialState: IInitialDrugDispensationState = {
    drugDispensation: null,
    loadState: {
        state: RequestState.None
    },
    saveState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDrugDispensationSelector: TypedUseSelectorHook<IDrugDispensationState> = useSelector;

export const DrugDispensationSelectors = {
    selectDrugDispensation: (state: IDrugDispensationState) => state.drugDispensation.drugDispensation,
    selectLoadState: (state: IDrugDispensationState) => state.drugDispensation.loadState,
    selectSaveState: (state: IDrugDispensationState) => state.drugDispensation.saveState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DrugDispensationReducer extends ImmerReducer<IInitialDrugDispensationState> {
    public loadById(id: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(drugDispensation?: Dtos.DrugDispensation) {
        this.draftState.drugDispensation = drugDispensation ? drugDispensation : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugDispensation = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public save(form: Dtos.DrugDispensation) {
        this.draftState.saveState = {
            state: RequestState.Pending
        };
    }

    public saveSuccess(drugDispensation?: Dtos.DrugDispensation) {
        this.draftState.drugDispensation = drugDispensation ? drugDispensation : null;
        this.draftState.saveState = {
            state: RequestState.Success
        };
    }

    public saveFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.saveState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DrugDispensationInitialState };
    }
};

export const DrugDispensationActions = createActionCreators(DrugDispensationReducer);
const reducer = createReducerFunction(DrugDispensationReducer, DrugDispensationInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DrugDispensationApi = {
    loadById: (id: number) => {
        return client.get(new Dtos.GetDrugDispensationById({
            id
        }));
    },
    save: (drugDispensation?: Dtos.DrugDispensation) => {
        return client.post(new Dtos.SetDrugDispensation({
            drugDispensation
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DrugDispensationLogic = {
    loadById: createLogic<IDrugDispensationState, {}, undefined, string, ReturnType<typeof DrugDispensationActions.loadById>>({
        type: DrugDispensationActions.loadById.type,
        process: async ({ action }, dispatch, done) => {
            const id = action.payload;

            try {
                const response = await DrugDispensationApi.loadById(id);

                dispatch(DrugDispensationActions.loadSuccess(
                    response.drugDispensation
                ));
            }
            catch (error: any) {
                dispatch(DrugDispensationActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    save: createLogic<IDrugDispensationState, {}, undefined, string, ReturnType<typeof DrugDispensationActions.save>>({
        type: DrugDispensationActions.save.type,
        process: async ({ action }, dispatch, done) => {
            const drugDispensation = action.payload;

            try {
                const response = await DrugDispensationApi.save(drugDispensation);

                dispatch(DrugDispensationActions.saveSuccess(
                    response.drugDispensation
                ));
            }
            catch (error: any) {
                dispatch(DrugDispensationActions.saveFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DrugDispensationLogic.loadById,
    DrugDispensationLogic.save
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDrugDispensation = (): [Dtos.DrugDispensation | null, IRequestState<Dtos.ResponseStatus>, IRequestState<Dtos.ResponseStatus>, typeof DrugDispensationActions] => {
    const drugDispensation = useSelector(DrugDispensationSelectors.selectDrugDispensation);
    const loadState = useSelector(DrugDispensationSelectors.selectLoadState);
    const saveState = useSelector(DrugDispensationSelectors.selectSaveState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DrugDispensationActions>(() => {
        return bindActionCreators(DrugDispensationActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DrugDispensationActions, dispatch));
    }, [dispatch, setActions]);

    return [
        drugDispensation,
        loadState,
        saveState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('drugDispensation', reducer as any, logics as any);
